import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, Subject, take } from 'rxjs';
import { UserPreferenceEnum } from 'src/app/modules/setting/setting/setting.component';
import { TabColumnFilter, TaskTabFilters } from 'src/app/shared/entity/utility-interfaces';
import { DataRepositoryService } from 'src/app/shared/services/data-repository/data-repository.service';
import { UserPreferencesService } from 'src/app/shared/services/user-preferences/user-preferences.service';

@Injectable({
  providedIn: 'root'
})
export class TaskbankColumnFilterService {
  columnFilterMap = {
    pbmOrderType: {
      columnKey: 'pbm_order_id',
      matchMode: 'startsWith'
    },
    taskStatus: {
      columnKey: 'task_state',
      matchMode: 'in'
    }
  }

  isSplitDragged = new BehaviorSubject(false);

  isSplitDragged$: Observable<boolean> = this.isSplitDragged.asObservable();

  private readonly filtersClearedSubject = new Subject<string>();

  // emits the tab name whenever its column filters are cleared, so open tabs can reset their table state
  filtersCleared$: Observable<string> = this.filtersClearedSubject.asObservable();

  constructor(
    private readonly userPreferenceService: UserPreferencesService,
    private readonly dataRepositoryService: DataRepositoryService
  ) { }

  /**
   * when user applies column filter in task bank this method is triggered to store those filters 
   * @param tabName - selected tab name
   * @param filterKey - the column name on which filter is applied
   * @param filterValue - filter value
  */
  saveColumnFilter(tabName: string, filterKey: keyof TabColumnFilter, filterValue: string | string[] | null): void {
    const updatedFilters = { ...this.dataRepositoryService.columnFilterRecord };

    if (filterValue === null || (Array.isArray(filterValue) && filterValue.length === 0)) {
      if (updatedFilters[tabName]) {
        delete updatedFilters[tabName][filterKey];
        if (Object.keys(updatedFilters[tabName]).length === 0) {
          delete updatedFilters[tabName];
        }
      }
    } else {
      updatedFilters[tabName] = {
        ...(updatedFilters[tabName] || {}),
        [filterKey]: filterValue
      };
    }

    this.userPreferenceService.saveUserPreference(UserPreferenceEnum.COLUMNFILTER, updatedFilters, false).pipe(take(1)).
      subscribe({
        next: () => {
          this.dataRepositoryService.columnFilterRecord = updatedFilters;
        },
        error: (_err) => {
        }
      });
  }

  /**
   * Checks whether the given tab currently has any column filter applied.
   * @param tabName - selected tab name
   */
  hasActiveFilters(tabName: string): boolean {
    const tabFilters = this.dataRepositoryService.columnFilterRecord?.[tabName];
    return !!tabFilters && Object.keys(tabFilters).length > 0;
  }

  /**
   * Clears all column filters saved for the given tab and notifies subscribers.
   * @param tabName - selected tab name
   */
  clearAllFilters(tabName: string): void {
    if (!this.hasActiveFilters(tabName)) return;
    const updatedFilters = { ...this.dataRepositoryService.columnFilterRecord };
    delete updatedFilters[tabName];

    this.userPreferenceService.saveUserPreference(UserPreferenceEnum.COLUMNFILTER, updatedFilters, false).pipe(take(1)).
      subscribe({
        next: () => {
          this.dataRepositoryService.columnFilterRecord = updatedFilters;
          this.filtersClearedSubject.next(tabName);
        },
        error: (_err) => {
        }
      });
  }

  /**
  * This method is called when a tab is selected with column filters to get all the saved column filter data.
  * it returns response as observable.
  */
  getColumnFilters$(): Observable<TaskTabFilters> {
    return this.userPreferenceService.getUserPreferenceByKey(UserPreferenceEnum.COLUMNFILTER).pipe(
      map((res: any) => {
        const filters = res?.preferenceValue ? JSON.parse(res.preferenceValue) : {};
        this.dataRepositoryService.columnFilterRecord = filters;
        return filters;
      })
    );
  }

  setIsSplitDragged() {
    this.isSplitDragged.next(true);
    setTimeout(() => this.isSplitDragged.next(false), 200);
  }
}